import { useState, useEffect } from "react";
import { Merge, CircleX, AlertTriangle, ArrowDown } from "lucide-react";
import {
  useDataProvider,
  useRecordContext,
  useGetManyReference,
  required,
  Form,
  useNotify,
  useRedirect,
  useTranslate,
} from "ra-core";
import type { Identifier } from "ra-core";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReferenceInput } from "@/components/admin/reference-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Contact } from "../types";
import { contactOptionText } from "../misc/ContactOption";
import {
  canAutoPreselect,
  findDuplicateContacts,
} from "../providers/commons/findDuplicateContacts";
import { resourceForContact, useContactResource } from "./contactResource";

export const ContactMergeButton = () => {
  const translate = useTranslate();
  const resource = useContactResource();
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        className="h-6 cursor-pointer"
        size="sm"
        onClick={() => setMergeDialogOpen(true)}
      >
        <Merge className="w-4 h-4" />
        {translate(`resources.${resource}.merge.action`, {
          _: "Merge with another contact",
        })}
      </Button>
      <ContactMergeDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
      />
    </>
  );
};

interface ContactMergeDialogProps {
  open: boolean;
  onClose: () => void;
}

const ContactMergeDialog = ({ open, onClose }: ContactMergeDialogProps) => {
  const loserContact = useRecordContext<Contact>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const resource = useContactResource();
  const dataProvider = useDataProvider();
  const [winnerId, setWinnerId] = useState<Identifier | null>(null);
  const [suggestedWinnerId, setSuggestedWinnerId] = useState<Identifier | null>(
    null,
  );
  const [isMerging, setIsMerging] = useState(false);
  const { mutateAsync } = useMutation({
    mutationKey: ["contacts", "merge", { loserId: loserContact?.id, winnerId }],
    mutationFn: async () => {
      return dataProvider.mergeContacts(loserContact?.id, winnerId);
    },
  });

  // Find potential duplicate contacts: matching name (case/accent-insensitive),
  // shared email, or shared phone number.
  const { data: duplicateCandidates } = useQuery({
    queryKey: ["contacts", "findDuplicateContacts", loserContact?.id],
    queryFn: () => findDuplicateContacts(loserContact as Contact, dataProvider),
    enabled: open && !!loserContact,
  });

  // Get counts of items to be merged
  const canFetchCounts = open && !!loserContact && !!winnerId;
  const { total: tasksCount } = useGetManyReference(
    "tasks",
    {
      target: "contact_id",
      id: loserContact?.id,
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: canFetchCounts },
  );

  const { total: notesCount } = useGetManyReference(
    "contact_notes",
    {
      target: "contact_id",
      id: loserContact?.id,
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: canFetchCounts },
  );

  const { total: referralsCount } = useGetManyReference(
    "contacts",
    {
      target: "referred_by_id",
      id: loserContact?.id,
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: canFetchCounts },
  );

  useEffect(() => {
    // Only a name or email match corroborates identity well enough to
    // auto-preselect. A phone-only match stays an option in the target
    // picker below (it lists every contact) but is never selected for you --
    // two people can share a club's landline.
    const strongCandidate = duplicateCandidates?.find(canAutoPreselect);
    if (strongCandidate) {
      setSuggestedWinnerId(strongCandidate.contact.id);
      setWinnerId(strongCandidate.contact.id);
    }
  }, [duplicateCandidates]);

  const handleMerge = async () => {
    if (!winnerId || !loserContact) {
      notify(`resources.${resource}.merge.select_target`, {
        type: "warning",
        messageArgs: {
          _: "Please select a contact to merge with",
        },
      });
      return;
    }

    try {
      setIsMerging(true);
      await mutateAsync();
      setIsMerging(false);
      notify(`resources.${resource}.merge.success`, {
        type: "success",
        messageArgs: {
          _: "Contacts merged successfully",
        },
      });
      redirect(`/${resourceForContact(loserContact!)}/${winnerId}/show`);
      onClose();
    } catch (error) {
      setIsMerging(false);
      notify(`resources.${resource}.merge.error`, {
        type: "error",
        messageArgs: {
          _: "Failed to merge contacts",
        },
      });
      console.error("Merge failed:", error);
    }
  };

  if (!loserContact) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="md:min-w-lg max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {translate(`resources.${resource}.merge.title`, {
              _: "Merge Contact",
            })}
          </DialogTitle>
          <DialogDescription>
            {translate(`resources.${resource}.merge.description`, {
              _: "Merge this contact with another one.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <p className="font-medium text-sm">
              {translate(`resources.${resource}.merge.current_contact`, {
                _: "Current Contact (will be deleted)",
              })}
            </p>
            <div className="font-medium text-sm mt-4">{contactOptionText}</div>

            <div className="flex justify-center my-4">
              <ArrowDown className="h-5 w-5 text-muted-foreground" />
            </div>

            <p className="font-medium text-sm mb-2">
              {translate(`resources.${resource}.merge.target_contact`, {
                _: "Target Contact (will be kept)",
              })}
            </p>
            <Form>
              <ReferenceInput
                source="winner_id"
                reference={resourceForContact(loserContact)}
                filter={{ "id@neq": loserContact.id }}
              >
                <AutocompleteInput
                  label=""
                  optionText={contactOptionText}
                  validate={required()}
                  onChange={setWinnerId}
                  defaultValue={suggestedWinnerId}
                  helperText={false}
                />
              </ReferenceInput>
            </Form>
          </div>

          {winnerId && (
            <>
              <div className="space-y-2">
                <p className="font-medium text-sm">
                  {translate(
                    `resources.${resource}.merge.what_will_be_merged`,
                    {
                      _: "What will be merged:",
                    },
                  )}
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  {notesCount != null && notesCount > 0 && (
                    <li>
                      • {notesCount} note
                      {notesCount !== 1 ? "s" : ""} will be reassigned
                    </li>
                  )}
                  {tasksCount != null && tasksCount > 0 && (
                    <li>
                      • {tasksCount} task
                      {tasksCount !== 1 ? "s" : ""} will be reassigned
                    </li>
                  )}
                  {referralsCount != null && referralsCount > 0 && (
                    <li>
                      • {referralsCount} contact
                      {referralsCount !== 1 ? "s" : ""} referred by this contact
                      will be updated
                    </li>
                  )}
                  {loserContact.email_jsonb?.length > 0 && (
                    <li>
                      • {loserContact.email_jsonb.length} email address
                      {loserContact.email_jsonb.length !== 1 ? "es" : ""} will
                      be added
                    </li>
                  )}
                  {loserContact.phone_jsonb?.length > 0 && (
                    <li>
                      • {loserContact.phone_jsonb.length} phone number
                      {loserContact.phone_jsonb.length !== 1 ? "s" : ""} will be
                      added
                    </li>
                  )}
                  {!notesCount &&
                    !tasksCount &&
                    !referralsCount &&
                    !loserContact.email_jsonb?.length &&
                    !loserContact.phone_jsonb?.length && (
                      <li className="text-muted-foreground/60">
                        {translate(
                          `resources.${resource}.merge.no_additional_data`,
                          {
                            _: "No additional data to merge",
                          },
                        )}
                      </li>
                    )}
                </ul>
              </div>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {translate(`resources.${resource}.merge.warning_title`, {
                    _: "Warning: Destructive Operation",
                  })}
                </AlertTitle>
                <AlertDescription>
                  {translate(
                    `resources.${resource}.merge.warning_description`,
                    {
                      _: "All data will be transferred to the second contact. This action cannot be undone.",
                    },
                  )}
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isMerging}>
            <CircleX />
            {translate("ra.action.cancel")}
          </Button>
          <Button onClick={handleMerge} disabled={!winnerId || isMerging}>
            <Merge />
            {isMerging
              ? translate(`resources.${resource}.merge.merging`, {
                  _: "Merging...",
                })
              : translate(`resources.${resource}.merge.confirm`, {
                  _: "Merge Contacts",
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
